import { TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import type {
  GapDraftView,
  GapRunPrecedent,
  GapRunValidation,
  RunGap,
} from '@momentum/types';
import { GapCategory, GapRunChronologyVerdict } from '@momentum/types';

import { useStickyDisclosure } from '../hooks/useStickyDisclosure';
import { verdictTone } from '../lib/run-audit';
import { doNowWarning, reportScores } from '../lib/run-report';
import { EvidenceList } from './EvidenceList';
import { InlineMarkdown } from './InlineMarkdown';
import { Prose } from './Prose';
import { SectionRule } from './SectionRule';

/** A heading inside a section, below the rule that opens it. */
function BlockHeading({ children }: { children: ReactNode }) {
  return <h3 className="gr-report-h">{children}</h3>;
}

/**
 * The two orientation fields, which describe the SCOPE rather than this gap.
 *
 * They hold the same text on every gap of a scope — the run writes them that way
 * on purpose, so a reader of one gap gets the whole framing without joining back
 * to the run document. Which means a reviewer walking the queue meets them again
 * on every draft, so the fold is sticky: fold it away once and it stays folded
 * for the rest of the run.
 *
 * Open by default all the same. The framing is what makes the claim below
 * legible on a scope the reviewer has not seen before, and a reviewer who
 * already knows the deal closes it in one click.
 */
function Orientation({ source }: { source: RunGap }) {
  const { isOpen, setOpen } = useStickyDisclosure(
    'run-report:orientation',
    true
  );

  if (
    source.projectNarrative === undefined &&
    source.contractFrame === undefined
  ) {
    return null;
  }

  return (
    <details
      className="gr-fold gr-report-orient"
      open={isOpen}
      onToggle={(toggleEvent) => setOpen(toggleEvent.currentTarget.open)}
    >
      <summary>Orientation · the engagement this gap sits in</summary>
      {/* `gr-fold-body` is `pre-wrap`, which would double every break `Prose`
          has already turned into a paragraph. */}
      <div className="gr-fold-body gr-fold-body-flow">
        {source.projectNarrative !== undefined && (
          <div>
            <BlockHeading>The deal</BlockHeading>
            <Prose text={source.projectNarrative} className="gr-prose" />
          </div>
        )}
        {source.contractFrame !== undefined && (
          <div className="gr-stack-top-lg">
            <BlockHeading>Contract frame</BlockHeading>
            <Prose text={source.contractFrame} className="gr-prose" />
          </div>
        )}
      </div>
    </details>
  );
}

/**
 * The gap's identity, and the four axes as they stand after adjudication.
 *
 * `metadata.id` is the fallback heading, not a decoration: a run persisted
 * before `title` existed has no heading at all, and the id is the one string
 * that always identifies the gap.
 */
function ReportHeader({ draft }: { draft: GapDraftView }) {
  const { source } = draft;
  const validation = source.validation;
  const scores = reportScores(source, validation);

  return (
    <header className="gr-report-head">
      <h1 className="gr-h1">{source.title ?? source.metadata.id}</h1>

      <div className="gr-row-meta gr-row-meta-centred">
        <span className="gr-chip mono">{source.metadata.id}</span>
        {validation !== undefined ? (
          <span className="gr-chip" data-tone={verdictTone(validation.verdict)}>
            {validation.verdict}
          </span>
        ) : (
          <span className="gr-chip" data-tone="amber">
            not adjudicated
          </span>
        )}
        <span className="gr-chip">
          {source.metadata.detector} · {source.claimType}
        </span>
        <span className="gr-chip">{source.metadata.foundBy}</span>
        <span className="gr-chip">run {source.metadata.runNumber}</span>
        <span className="gr-chip">confidence {source.confidence}</span>
        {source.isHypothesis === true && (
          <span className="gr-chip" data-tone="amber">
            hypothesis
          </span>
        )}
        {source.variant === GapCategory.SALES && source.requiresBaseRate && (
          <span className="gr-chip">requires base rate</span>
        )}
      </div>

      <div className="gr-report-scores">
        {scores.map((score) => (
          <div
            className="gr-report-score"
            data-direction={score.direction}
            key={score.axis}
          >
            <span className="gr-report-score-k">{score.label}</span>
            <span className="gr-report-score-v">
              {score.value}
              {score.revisedFrom !== undefined && (
                <i>
                  {score.direction === 'up' ? '↑' : '↓'}
                  {score.revisedFrom}
                </i>
              )}
            </span>
          </div>
        ))}
      </div>
    </header>
  );
}

/**
 * `CURRENT_FACT` is the only verdict that leaves the claim standing on its own
 * facts. Every other value — `CHRONOLOGY_UNKNOWN` included — means a reviewer
 * has to go and check the timeline themselves.
 */
function chronologyTone(verdict: GapRunChronologyVerdict): string {
  return verdict === GapRunChronologyVerdict.CURRENT_FACT ? 'emerald' : 'amber';
}

/**
 * The validator's checks, below its evidence.
 *
 * Three of the four are sentences, so they stack rather than sitting in a
 * key-on-the-left row. The three optional ones render nothing when the validator
 * did not write them — an empty "base rate:" row would read as a base rate of
 * nothing rather than as a check that was never made.
 */
function ValidatorChecks({ validation }: { validation: GapRunValidation }) {
  return (
    <div className="gr-facts gr-facts-stacked gr-stack-top-lg">
      {validation.baseRate !== undefined && (
        <div className="gr-fact">
          <span>base rate</span>
          {/* A written ratio ("absent-and-hurt 3/14"), not a number — the
              denominator is what makes it mean anything. */}
          <span className="gr-fact-v mono">{validation.baseRate}</span>
        </div>
      )}
      {validation.cheapestCheck !== undefined && (
        <div className="gr-fact">
          <span>cheapest check</span>
          <span className="gr-fact-v">{validation.cheapestCheck}</span>
        </div>
      )}
      {validation.precedentCheck !== undefined && (
        <div className="gr-fact">
          <span>precedent check</span>
          <span className="gr-fact-v">{validation.precedentCheck}</span>
        </div>
      )}
      <div className="gr-fact">
        <span>chronology verdict</span>
        <span className="gr-fact-v">
          <span
            className="gr-chip"
            data-tone={chronologyTone(validation.chronologyVerdict)}
          >
            {validation.chronologyVerdict}
          </span>
        </span>
      </div>
    </div>
  );
}

/**
 * The adjudication, in the order SPEC §4.4 fixes: the verdict, then the case
 * against, then the case for. Against leads because the case for is the claim
 * the reviewer has already read a section above.
 */
function ValidatorVerdict({ validation }: { validation: GapRunValidation }) {
  return (
    <>
      <div className="gr-verdict" data-verdict={validation.verdict}>
        <TriangleAlert /> {validation.verdict}
      </div>
      <Prose text={validation.headline} className="gr-prose gr-stack-top" />

      <div className="gr-report-block">
        <BlockHeading>
          Against · {validation.evidenceAgainst.length}
        </BlockHeading>
        {validation.evidenceAgainst.length > 0 ? (
          <EvidenceList evidence={validation.evidenceAgainst} marker="—" />
        ) : (
          <div className="gr-empty">Nothing recorded against this claim.</div>
        )}
      </div>

      <div className="gr-report-block">
        <BlockHeading>For · {validation.evidenceFor.length}</BlockHeading>
        {validation.evidenceFor.length > 0 ? (
          <EvidenceList
            evidence={validation.evidenceFor}
            marker="+"
            tone="for"
          />
        ) : (
          <div className="gr-empty">Nothing recorded for this claim.</div>
        )}
      </div>

      <ValidatorChecks validation={validation} />
    </>
  );
}

/**
 * A past deal where the same mistake already played out.
 *
 * The documented cost and the transfer mechanism are the two lines that make a
 * precedent worth citing — one says what it cost last time, the other says why
 * this deal is exposed to the same thing — so neither is summarised away. Each
 * precedent keeps its own evidence, on the same terms as the claim's.
 */
function Precedent({ precedent }: { precedent: GapRunPrecedent }) {
  return (
    <li>
      <time>
        {precedent.dealName} · {precedent.outcome}
      </time>
      <p>{precedent.whatHappened}</p>
      <div className="gr-facts gr-facts-stacked gr-facts-flush">
        <div className="gr-fact">
          <span>documented cost</span>
          <span className="gr-fact-v">{precedent.documentedCost}</span>
        </div>
        <div className="gr-fact">
          <span>why it transfers</span>
          <span className="gr-fact-v">{precedent.transferMechanism}</span>
        </div>
        <div className="gr-fact">
          <span>deal record</span>
          <span className="gr-fact-v mono">{precedent.externalId}</span>
        </div>
      </div>
      {precedent.evidence.length > 0 && (
        <div className="gr-stack-top">
          <EvidenceList
            evidence={precedent.evidence}
            marker="·"
            tone="neutral"
          />
        </div>
      )}
    </li>
  );
}

/**
 * The run's own gap, rendered as the pipeline wrote it.
 *
 * This is the PIPELINE zone whole — every value comes off `source` and none of
 * it falls back to `gap.*` (see `product-value.ts`). It is the counterpart to
 * `ReaderDoc`, which reads the product layer being assembled: the same gap,
 * before any of the enrichment.
 *
 * Sections that the run left empty are not rendered at all, unlike the reader's
 * fixed order. The reader holds its order because a reviewer walking twenty
 * drafts needs the claim in the same place every time and an unwritten product
 * field is work somebody still owes; here an absent field is not a hole — the
 * run is frozen, and nobody is going to fill it in.
 *
 * English only. A `RunGap` carries no translations, and there is nothing to
 * translate: every string on the page was written by an agent in English.
 */
export function RunReportDoc({ draft }: { draft: GapDraftView }) {
  const { source } = draft;
  const validation = source.validation;
  const doNowBanner = doNowWarning(validation);
  const precedents = source.precedents ?? [];

  return (
    <article className="gr-doc">
      <Orientation source={source} />

      <ReportHeader draft={draft} />

      <SectionRule label="The gap" />
      <div className="gr-claim">
        <b>Claim:</b> {source.claim}
      </div>

      <div className="gr-report-block">
        <BlockHeading>Rests on · {source.evidence.length}</BlockHeading>
        {source.evidence.length > 0 ? (
          <EvidenceList evidence={source.evidence} marker="·" tone="neutral" />
        ) : (
          <div className="gr-empty">
            <b>No citations</b>The finder recorded no evidence for this claim.
          </div>
        )}
      </div>

      <div className="gr-report-block">
        <BlockHeading>Became flaggable</BlockHeading>
        <div className="gr-facts gr-facts-flush">
          <div className="gr-fact">
            <span>drift date</span>
            <span className="gr-fact-v mono">{source.drift.date}</span>
          </div>
          <div className="gr-fact">
            <span>record</span>
            <span className="gr-fact-v mono">{source.drift.externalId}</span>
          </div>
        </div>
        <Prose
          text={source.drift.reasoning}
          className="gr-prose gr-stack-top-sm"
        />
      </div>

      <SectionRule label="Validator" />
      {validation !== undefined ? (
        <ValidatorVerdict validation={validation} />
      ) : (
        <div className="gr-note" data-tone="amber">
          <TriangleAlert />
          <div>
            <strong>Never adjudicated.</strong> No validator verdict reached
            this gap, so nothing above has been checked against the evidence —
            the claim, and the four scores, are the finder&apos;s alone. The run
            audit&apos;s coverage section names the batch that failed.
          </div>
        </div>
      )}

      {(precedents.length > 0 || source.precedentSearchNote !== undefined) && (
        <>
          <SectionRule label="Precedents" />
          {precedents.length > 0 ? (
            <ol className="gr-timeline">
              {precedents.map((precedent) => (
                <Precedent key={precedent.externalId} precedent={precedent} />
              ))}
            </ol>
          ) : (
            // The disclosed search behind an empty list — what was looked for
            // and not found, which is a finding rather than an absence.
            <div className="gr-prose gr-prose-quiet">
              <Prose text={source.precedentSearchNote ?? ''} />
            </div>
          )}
        </>
      )}

      {source.doNow !== undefined && (
        <>
          <SectionRule label="Do now" />
          {doNowBanner !== undefined && (
            <div className="gr-note gr-stack-top" data-tone={doNowBanner.tone}>
              <TriangleAlert />
              <div>{doNowBanner.text}</div>
            </div>
          )}
          <div className="gr-card gr-stack-top">
            <div className="gr-card-v">
              <Prose text={source.doNow} />
            </div>
          </div>
        </>
      )}

      <SectionRule label="How this was found" />
      {/* `InlineMarkdown` rather than `Prose`: `gr-fold-body` is `pre-wrap`, so
          the blank lines these fields carry already read as paragraphs and only
          the inline tokens inside them are left to render. */}
      <div className="gr-stack">
        {validation !== undefined && (
          <details className="gr-fold">
            <summary>Search method disclosure</summary>
            <div className="gr-fold-body">
              <InlineMarkdown text={validation.searchMethodDisclosure} />
            </div>
          </details>
        )}
        <details className="gr-fold">
          <summary>Account overview</summary>
          <div className="gr-fold-body">
            <InlineMarkdown text={source.accountOverview} />
          </div>
        </details>
        <details className="gr-fold">
          <summary>Project overview</summary>
          <div className="gr-fold-body">
            <InlineMarkdown text={source.projectOverview} />
          </div>
        </details>
      </div>
    </article>
  );
}
